import { type FC, type ReactNode, useEffect, useState } from "react";

import { mergeClasses } from "@huub-web/utils";

import {
  AgentAvatarController,
  Center,
  ChatHistoryList,
  ChatView,
  type ChatViewProps,
  Chip,
  Flex,
  Reveal,
  ScrollPane,
  Spacing,
  Stack,
  nextFrame,
  useInitializedEffect,
  usePubSub,
} from "@huub-web/ui";

import { useNavigationState } from "../../../providers";
import type {
  AgentState,
  ChatConversation,
  ChatHistoryOverviewItem,
} from "@huub-web/ui/types";
import type { ThemeName } from "@huub-web/ui/theme";

import {
  SIDE_CONTENT_SCROLL_EMIT_TAG,
  type SideAgentSlide,
} from "../SideAgentContent";
import { Header } from "./Header";

import {
  agentAvatarStyle,
  bottomFillerStyle,
  contentStyle,
  revealStyle,
  scrollPaneStyle,
  sidePaddingStyle,
  stackStyle,
} from "./InnerContent.css";

type InnerContentProps = {
  slide?: SideAgentSlide;
  conversation?: ChatConversation;
  state?: AgentState;
  trailing?: ReactNode;
  footer?: ReactNode;
  slides?: Record<SideAgentSlide, ReactNode>;
  size?: "s" | "m";
  debug?: boolean;
  theme?: ThemeName;
  variant?: ChatViewProps["variant"];
  mapAIResponseTrailing?: ChatViewProps["mapAIResponseTrailing"];
  onHistoryChatClick?: (item: ChatHistoryOverviewItem) => void;
  onSlideChange?: (slide: SideAgentSlide) => void;
  onBackButtonClick?: () => void;
};

/**
 * Inner content component for the side agent panel
 */
export const InnerContent: FC<InnerContentProps> = ({
  slide: slideProp = "agent",
  variant = "default",
  slides,
  conversation,
  state,
  trailing,
  footer,
  theme,
  debug = false,
  size = "m",
  onHistoryChatClick,
  onSlideChange,
  onBackButtonClick,
  mapAIResponseTrailing,
}) => {
  const { sideContentVariant, navigationContext } = useNavigationState();
  const pubSub = usePubSub();

  // Slides
  // ------

  const [slide, setSlide] = useState<SideAgentSlide>(slideProp);

  useInitializedEffect(() => {
    onSlideChange?.(slide);
  }, [slide]);

  useEffect(() => {
    setSlide(slideProp);
  }, [slideProp]);

  // Start on bottom

  useEffect(() => {
    if (slide === "agent") {
      pubSub.publish("scrollToBottom", {
        tag: SIDE_CONTENT_SCROLL_EMIT_TAG,
        instant: true,
      });
    }
  }, [slide]);

  const handleBackButtonClick = () => {
    setSlide("agent");
    onBackButtonClick?.();
  };

  useEffect(
    () =>
      pubSub.subscribe("sideContent", ({ action, id }) => {
        if (action === "open" && id) {
          nextFrame(() => setSlide(id));
        }
      }),
    [],
  );

  return (
    <Flex
      className={contentStyle}
      gap={variant === "compact" ? "s" : "m"}
      direction="column"
    >
      <Stack className={stackStyle} overflowHidden>
        <Reveal className={revealStyle({ gap: "xs", variant })}>
          {slide === "agent" && (
            <>
              <Center
                className={mergeClasses(
                  sidePaddingStyle,
                  agentAvatarStyle({ variant }),
                )}
                horizontal
              >
                <AgentAvatarController
                  state={state}
                  size={size === "s" ? 150 : 200}
                  theme={theme}
                />
              </Center>

              {debug && navigationContext && navigationContext.length > 0 && (
                <>
                  <Flex gap="xxs" style={{ paddingRight: "24px" }} wrap>
                    {navigationContext?.map((item) => (
                      <Chip
                        variant="error"
                        style={{ background: "red", maxWidth: "220px" }}
                        key={item}
                      >
                        {item}
                      </Chip>
                    ))}
                  </Flex>
                  <Spacing />
                </>
              )}

              <ScrollPane
                overlapColor={
                  variant === "compact" ? "background" : "navigation"
                }
                overlapSize="s"
                className={scrollPaneStyle}
                contentClassName={sidePaddingStyle}
                emitTag={SIDE_CONTENT_SCROLL_EMIT_TAG}
              >
                <ChatView
                  allowSizing={variant === "sideContent"}
                  debug={debug}
                  conversation={conversation}
                  variant={variant}
                  showTime={false}
                  showAuthor={false}
                  mapAIResponseTrailing={mapAIResponseTrailing}
                  trailing={
                    <>
                      {trailing}
                      {variant === "sideContent" && (
                        <div
                          className={bottomFillerStyle({
                            variant: sideContentVariant,
                          })}
                        />
                      )}
                    </>
                  }
                />
              </ScrollPane>
            </>
          )}
        </Reveal>

        <Reveal className={revealStyle({ gap: "m", variant })}>
          {slide === "history" && (
            <>
              <Header back onBackButtonClick={handleBackButtonClick} />
              <ChatHistoryList
                variant={variant}
                onChatClick={onHistoryChatClick}
              />
            </>
          )}
        </Reveal>

        {slides &&
          Object.entries(slides).map(([key, item]) => (
            <Reveal key={key} className={revealStyle({ gap: "m", variant })}>
              {slide === key && (
                <>
                  <Header back onBackButtonClick={() => setSlide("agent")} />
                  {item}
                </>
              )}
            </Reveal>
          ))}
      </Stack>

      {footer && (
        <Flex
          gap="xs"
          align="center"
          justify={footer ? "space-between" : "flex-end"}
          className={sidePaddingStyle}
        >
          {footer}
        </Flex>
      )}
    </Flex>
  );
};
